import type {AllocationStatus, JobScheduleKind, JobTimeSpan, ScheduleDate} from './types'
import {formatSpanTimeRange, SLOTS_PER_DAY, spanStartLabel} from './work-day'

type SpanOptions = {
    dayOffset?: number
    startSlotIndex?: number
    endSlotIndex?: number
}

/** Build canonical span storage from a schedule kind. */
export function spanForScheduleKind(kind: JobScheduleKind, options: SpanOptions = {}): JobTimeSpan {
    const dayOffset = options.dayOffset ?? 0

    switch (kind) {
        case 'one_day':
            return {
                dayOffset,
                startSlotIndex: 0,
                endSlotIndex: SLOTS_PER_DAY - 1,
            }
        case 'two_day':
            return {
                dayOffset,
                startSlotIndex: 0,
                endSlotIndex: SLOTS_PER_DAY - 1,
                continuesIntoNextDay: true,
            }
        case 'timed':
            return {
                dayOffset,
                startSlotIndex: options.startSlotIndex ?? 4,
                endSlotIndex: options.endSlotIndex ?? 6,
            }
    }
}

export function scheduleKindLabel(kind: JobScheduleKind): string {
    switch (kind) {
        case 'one_day':
            return '1 Day'
        case 'two_day':
            return '2 Day'
        case 'timed':
            return 'Timed'
    }
}

export function formatJobScheduleLabel(
    scheduleKind: JobScheduleKind,
    span?: JobTimeSpan,
    day?: ScheduleDate,
): string {
    if (scheduleKind === 'one_day') {
        if (day) {
            return `${day.weekdayShort}, ${day.monthLabel} ${day.dayOfMonth} · 1 Day`
        }
        return '1 Day'
    }

    if (scheduleKind === 'two_day') {
        if (day) {
            return `${day.weekdayShort}, ${day.monthLabel} ${day.dayOfMonth} · 2 Day`
        }
        return '2 Day'
    }

    return formatSpanTimeRange(span, day)
}

export function jobNeedsAttention(job: {
    status: 'unassigned' | 'assigned'
    allocationStatus?: AllocationStatus
}): boolean {
    return job.status === 'unassigned'
}

/** Slot range for bar rendering in the projected timeline grid. */
export function barSlotRange(
    scheduleKind: JobScheduleKind,
    span: JobTimeSpan | undefined,
    slotsPerDay: number,
): {startSlotIndex: number; slotCount: number} {
    if (scheduleKind === 'one_day') {
        return {
            startSlotIndex: span.startSlotIndex,
            slotCount: slotsPerDay,
        }
    }

    if (scheduleKind === 'two_day') {
        return {
            startSlotIndex: span.startSlotIndex,
            slotCount: slotsPerDay * 2,
        }
    }

    return {
        startSlotIndex: span.startSlotIndex,
        slotCount: span.endSlotIndex - span.startSlotIndex + 1,
    }
}

export function timedJobStartLabel(span: JobTimeSpan): string {
    return spanStartLabel(span)
}
